import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

// local imports & assets
import calendarLoading from 'assets/icons/loading-calendar.png'
import AuthUtil from 'utils/AuthUtil'
import DateUtil from 'utils/DateUtil'
import {
  IFilteredGymGroupList,
  IFilteredGymSalonList,
  IFilteredGymTrainerList,
  IGymSalonFilteredReq,
  GymListTypes,
} from 'types/GymType'
import useGetGymLocationList from '../hooks/use-get-gym-locations'
import useGetGymFilteredList from '../hooks/use-get-gym-filtered-lists'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import GymSalonFilterForm from './GymSalonFilterForm'
import GymSalonSearch from './GymSalonSearch'
import GymListTypeRadio from './GymListTypeRadio'
import GymPersonalCardContainer from './personal/GymPersonalCardContainer'
import GymGroupCardContainer from './group/GymGroupCardContainer'
import GymTrainerCardContainer from './trainer/GymTrainerCardContainer'
import { Divider, FormLayoutWrapper, Line, GymCardListContainer } from './styled'

/* filtered date - default */
const TODAY = DateUtil.getStartOfDay(new Date())

export default function GymSalonList() {
  const [locationIds, setLocationIds] = useState<number[] | []>([])

  /* Get Current logged employee infos - (location id) */
  const { data: locationList, isLoading } = useGetGymLocationList()

  useEffect(() => {
    const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId || 0
    const empLocationIsExist = locationList?.map((o: any) => o.id).includes(employeeLocation)
    setLocationIds(() => (empLocationIsExist ? [employeeLocation] : []))
    setValue('locationIds', empLocationIsExist ? [employeeLocation] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList])

  /* Filter form default values */
  const defaultValues: IGymSalonFilteredReq = { locationIds: locationIds, reservationDate: TODAY }

  /*  Search and filter from gym lists */
  const [searchText, setSearchText] = useState('')
  const [filteredSalon, setFilteredSalon] = useState<any[] | []>([])

  /* Filter form state  */
  const { control, reset, handleSubmit, formState, watch, getValues, setValue } = useForm<IGymSalonFilteredReq>({
    defaultValues,
  })
  const reservationDate = watch('reservationDate') || TODAY

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1

  /* Get Gym Salon List Get Gym Salon - Locations | Custom Hooks */
  const { getGymList, gymList, loading, listType, handleChangeListType } = useGetGymFilteredList<
    IFilteredGymSalonList | IFilteredGymGroupList | IFilteredGymTrainerList
  >(defaultValues, handleResetForm, isLoading)

  /* Handle Form Submit */
  const onSubmit = (data: IGymSalonFilteredReq) => getGymList(data)
  const reSubmitForm = () => getGymList(getValues())

  /* Handle Reset Filter Form */
  function handleResetForm() {
    reset(
      { locationIds: locationIds, reservationDate: TODAY },
      { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false }
    )
    !!submitCount && onSubmit(defaultValues)
    setSearchText('')
  }

  /* Render gymPersonalList or filtered Gym Salons list */
  const renderList = searchText ? filteredSalon : gymList
  const showCapacity = !!reservationDate

  /* Render Gym card list types */
  function renderCardList(listType: GymListTypes) {
    const types = {
      personal: (
        <GymPersonalCardContainer
          filteredDate={reservationDate}
          renderList={renderList}
          showCapacity={showCapacity}
          runAfterSubmit={reSubmitForm}
        />
      ),
      group: <GymGroupCardContainer renderList={renderList} runAfterSubmit={reSubmitForm} />,
      trainer: <GymTrainerCardContainer filteredDate={reservationDate} renderList={renderList} />,
    }
    return <>{!!renderList?.length ? <> {types[listType]}</> : <NoContent />}</>
  }

  /* Show full-screen loader only on initial load (when list is empty).
     During background refresh after a reservation, keep cards/notification mounted
     so the success dialog can appear and counts update in place. */
  if ((loading || isLoading) && !gymList.length) return <ListLoader />

  return (
    <div className="campus-services">
      {/* Filter Form */}
      <GymListTypeRadio handleChange={handleChangeListType} selectedType={listType} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <GymSalonSearch
            salons={gymList}
            setData={setFilteredSalon}
            searchText={searchText}
            setSearchText={setSearchText}
            type={listType}
          />
          <Line className="pi pi-ellipsis-h" />
          <GymSalonFilterForm
            control={control}
            locationList={locationList}
            disableResetBtn={disableResetBtn}
            disableSubmitBtn={disableSubmitBtn}
            handleReset={handleResetForm}
          />
        </FormLayoutWrapper>
      </form>

      <Divider />
      {/* Card List — keep mounted during refresh so the success notification
          dialog (hosted inside the card container) is not unmounted. */}
      <GymCardListContainer>{renderCardList(listType)}</GymCardListContainer>
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}
